<template>
  <v-card flat class="mt-1">
    <v-slide-y-transition>
      <v-card-title v-if="showGridFilter">
        <v-layout row wrap>
          <v-flex sm6>
            <v-text-field append-icon="filter_list" :label="messages.label.ASSET_FILTER" single-line hide-details
              v-model="search">
            </v-text-field>
          </v-flex>
        </v-layout>
      </v-card-title>
    </v-slide-y-transition>
    <v-card-text class="py-1">
      <v-data-table :headers="headers" :items="searchResult" :search="search" :pagination.sync="pagination"
        :loading="assetsLoading">
        <template slot="items" slot-scope="props">
          <td>
            <v-btn fab dark small color="primary" :disabled="assetsLoading" @click="editXML(props.item)">
              <v-icon dark>edit</v-icon>
            </v-btn>
          </td>
          <td class="text-xs-left">{{ props.item.packageAssetId }}</td>
          <td class="text-xs-left">{{ props.item.providerName }}</td>
          <td class="text-xs-left">{{ props.item.title }}</td>
          <td class="text-xs-left">{{ props.item.licenseWindowStartDate }}</td>
          <td class="text-xs-left">{{ props.item.licenseWindowEndDate }}</td>
          <td class="text-xs-left">{{ props.item.destinationPlatform }}</td>
          <td class="text-xs-left">{{ props.item.seriesId }}</td>
          <td class="text-xs-left">{{ props.item.seriesName }}</td>
          <td class="text-xs-left">{{ getLocalDateTime(props.item.lastModifiedOnDateTime) }}</td>
          <td class="text-xs-left">{{ props.item.movieContentFileName }}</td>
        </template>
        <template slot="no-data">
          <v-alert :value="true" :color="initiatedSearch ? 'error' : 'info'"
            :class="{ 'black--text': !initiatedSearch, 'white--text': initiatedSearch }"
            :icon="!initiatedSearch ? 'info' : 'warning'">
            <span v-if="!initiatedSearch">
              {{ messages.info.SELECT_SEARCH_CRITERIA }}
            </span>
            <span v-else>
              {{ messages.info.NO_SEARCH_RESULTS }}
            </span>
          </v-alert>
        </template>
        <template slot="no-results">
          <v-alert :value="true" color="info" class="black--text" icon="warning">
            {{ messages.info.NO_FILTER_RESULTS }}
          </v-alert>
        </template>
        <template slot="pageText" slot-scope="{ pageStart, pageStop }">
          From {{ pageStart }} to {{ pageStop }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from "axios"
import queryString from "query-string"
import XMLBacklogService from "@/XMLBacklog/XMLBacklogService"
import * as utils from "@/Shared/utils"
import messages from "@/XMLBacklog/messages.json"

export default {
  name: "XMLBacklogSearchResult",
  data() {
    return {
      max25chars: (v) => v.length <= 25 || "Input too long!",
      tmp: "",
      search: "",
      showResult: false,
      assetsLoading: false,
      initiatedSearch: false,
      pagination: {
        sortBy: "column",
        page: 1,
        rowsPerPage: 10,
        descending: false,
        totalItems: 0
      },
      headers: this.getTableHeaders(),
      searchResult: [],
      searchParams: {},
      selectedAsset: {}
    }
  },
  computed: {
    showGridFilter() {
      return this.searchResult.length > 0
    }
  },
  methods: {
    /**
     * Search assets
     * httpStatus                statusCode statusMessage                         message
     *  ------------------------- ---------- ------------------------------------- ---------------------------
     *  200 OK                    0
     *  204 NO_CONTENT            4        No results
     *  400 BAD_REQUEST           1001500  PackageAssetId is required
     *  500 INTERNAL_SERVER_ERROR 1000100  Error                                   (various errors)
     *  503 SERVICE_UNAVAILABLE   1001100  Error accessing database                (various DB errors)
     *  503 SERVICE_UNAVAILABLE   1000700  Unable to access system                 (various network errors)
     */
    searchAssets: function (searchParams) {
      this.searchParams = searchParams
      let assetMetadatasURI =
        utils.getServerUrl() + "/api/xml-backlog/assetMetadatas"
      if (searchParams.assetIds.length > 0) {
        this.assetsLoading = true
        assetMetadatasURI +=
          "?" +
          queryString.stringify({
            id: searchParams.assetIds,
            p: searchParams.providers
          })
        axios
          .get(assetMetadatasURI)
          .then((response) => {
            this.searchResult = response.data.data.assetMetadataList
            this.assetsLoading = false
            this.initiatedSearch = true
          })
          .catch(() => {
            this.searchResult = []
            this.assetsLoading = false
            this.initiatedSearch = true
          })
      }
    },
    /**
     * Open XML Editor to start editing the XML
     */
    editXML: function (selectedAsset) {
      XMLBacklogService.$emit("event:loadAssetMetadata", selectedAsset.xmlUid)
      this.selectedAsset = selectedAsset
    },
    /**
     * Update row with stale XML details with latest metadata
     */
    updateStaleXMLRow(newXMLMetadata) {
      this.searchResult.forEach((element) => {
        if (element.xmlUid === this.selectedAsset.xmlUid) {
          let index = this.searchResult.indexOf(element)
          this.searchResult[index] = newXMLMetadata
        }
      })
    },
    getTableHeaders() {
      let headers = [
        {
          text: "",
          sortable: false
        },
        {
          text: messages.label.TH_ASSET_ID,
          align: "left",
          value: "packageAssetId"
        },
        {
          text: messages.label.TH_PROVIDER,
          align: "left",
          value: "providerName"
        },
        {
          text: messages.label.TH_TITLE,
          align: "left",
          value: "title",
          width: "40%"
        },
        {
          text: messages.label.TH_START_DATE,
          align: "left",
          value: "licenseWindowStartDate"
        },
        {
          text: messages.label.TH_END_DATE,
          align: "left",
          value: "licenseWindowEndDate"
        },
        {
          text: messages.label.TH_DESTINATION_PLATFORM,
          align: "left",
          value: "destinationPlatform"
        },
        {
          text: messages.label.TH_SERIES_ID,
          align: "left",
          value: "seriesId"
        },
        {
          text: messages.label.TH_SERIES_NAME,
          align: "left",
          value: "seriesName"
        },
        {
          text: messages.label.TH_LAST_UPDATE,
          align: "left",
          value: "lastModifiedOnDateTime"
        },
        {
          text: messages.label.TH_MPEG_FILE_NAME,
          align: "left",
          value: "movieContentFileName"
        }
      ]

      return headers
    },

    /**
     * Get local time
     */
    getLocalDateTime(dateTime) {
      return utils.getLocalDateTime(dateTime)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    XMLBacklogService.$on("event:assetSerach", (searchParams) => {
      this.searchAssets(searchParams)
    })
    XMLBacklogService.$on("event:updateStaleXMLRow", (xmlMetadata) => {
      this.updateStaleXMLRow(xmlMetadata)
    })
    XMLBacklogService.$on("event:refreshSearchResult", () => {
      this.searchAssets(this.searchParams)
    })
  },
  beforeDestroy() {
    XMLBacklogService.$off("event:assetSerach")
    XMLBacklogService.$off("event:updateStaleXMLRow")
    XMLBacklogService.$off("event:refreshSearchResult")
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/XMLBacklog/XMLBacklogSearchResult.vue